import React, { useRef, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { commonStyles } from "../../../../common/commonStyles";
import { eventSwitchSx } from "../../../../common/dashboard/switchStyles";
import RichTextEditor from "../../../../common/FormComponents/RichTextEditor";
import { CmpEventKindEnum, CmpEventLibraryKindEnum } from "../../../../../graphql/types";
import {
  EventDressCode,
  EventRegistrationQuestion,
  EventRegistrationQuestionChoice,
  EventSpeaker,
  createDefaultRegistrationQuestions,
} from "../../eventMockData";
import { IdaGenerateControl } from "../../../Members/memberWorkflowShared";
import IdaGenerateDialog from "../../../../common/dashboard/IdaGenerateDialog";
import { EVENT_IDA_FIELDS, useEventIdaGeneration } from "./useEventIdaGeneration";
import {
  FieldLabel,
  WizardActionButton,
  WizardSectionCard as SectionCard,
  WizardSectionHeading as SectionHeading,
  WizardStepProps,
  isEventKindLocked,
  shouldShowDuplicateEventNotice,
  wizardId,
  wizardTextFieldSx,
} from "./wizardShared";
import EventLibrarySelect, {
  DeleteConfirmModal,
  deletedMessage,
  SuccessPopup,
} from "./EventLibrarySelect";
import EventRegistrationRequirementsControl from "./EventRegistrationRequirementsControl";
import EventTypeSelect from "./EventTypeSelect";
import EventPartnersSection from "./EventPartnersSection";
import { EventReorderHandle, useEventRowReorder } from "./useEventRowReorder";
import { resolveEventSpeakerKeynotes, setEventSpeakerKeynote } from "../../eventSpeakerKeynote";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import { ImageFrameCropGuide, ImageFrameField, frameImageAtCentre } from "../../../../common/dashboard/ImageFrameField";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import { hasPreselectMeals, normalizeEventButtonUrl } from "../../eventBackendMappers";
import { enterFieldMessage } from "../../../../../utils/fieldMessages";

const dressCodes: EventDressCode[] = ["Formal", "Business Attire", "Casual"];

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

// Fallback presets for events created before the registrationQuestions
// form-state field existed. createBlankEventFormState seeds new events with
// the same presets, so this only renders for legacy/empty data.
const DEFAULT_REGISTRATION_QUESTIONS: EventRegistrationQuestion[] =
  createDefaultRegistrationQuestions();

// The Speaker Question helper is UI copy, not saved data (Claude Design (8)
// Create Event Screen.dc.html:532), so it always comes from the preset.
const SPEAKER_QUESTION_HELPER =
  DEFAULT_REGISTRATION_QUESTIONS.find((question) => question.kind === "speakerQuestion")?.description || "";

// Question option rows dim to 0.45 while gated (design mkToggle, :2080-2093).
const questionChoiceSx = (gated: boolean) => ({
  m: 0, gap: "10px", opacity: gated ? 0.45 : 1,
  "& .MuiCheckbox-root": { p: 0 },
  "& .MuiFormControlLabel-label, & .MuiFormControlLabel-label.Mui-disabled": { fontSize: 12, color: "#0A0A0A" },
});

// Blank third column in the questions grid (design :458-461, 480, 526).
const questionGridClassName = "grid grid-cols-1 items-start gap-[16px] lg:grid-cols-[minmax(0,1fr)_220px_120px]";

// ---------------------------------------------------------------------------
// Image dropzone (EVT-CREATE-004): click-to-browse AND drag/drop read the file
// via FileReader.readAsDataURL and store the data URL on the event state.
// ---------------------------------------------------------------------------

const isRenderableImage = (value: string) =>
  /^(data:image|https?:|blob:|\/)/.test(value);

// The website event hero is full width and at least 520 px high with a centred
// cover crop (chamber-member-pro-website Events.client.tsx EventHero). On a
// 1440 px desktop it shows about the middle 2.77:1 band of a 16:9 banner; on a
// 390 px phone about the middle 0.75:1 column (Monday extra task, Moeen Q10).
const HERO_DESKTOP_RATIO = 1440 / 520;
const HERO_PHONE_RATIO = 390 / 520;
export const getEventHeroCropGuides = (bannerRatio: number): ImageFrameCropGuide[] => [
  { label: "Website (desktop)", widthFraction: 1, heightFraction: Math.min(1, bannerRatio / HERO_DESKTOP_RATIO) },
  { label: "Phone", widthFraction: Math.min(1, HERO_PHONE_RATIO / bannerRatio), heightFraction: 1 },
];

const ImageDropzone = ({
  value,
  onChange,
  compact = false,
  aspectRatio,
  outputWidth,
  alt,
  cropGuides,
}: {
  value: string;
  onChange: (dataUrl: string) => void;
  compact?: boolean;
  /** Frame shape for the Edit (reframe) control. */
  aspectRatio: number;
  outputWidth: number;
  alt: string;
  cropGuides?: readonly ImageFrameCropGuide[];
}): React.ReactElement => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const readFile = (file: File | null | undefined) => {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setUploadError("This file is not a PNG or JPG image. Choose a PNG or JPG image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("This image is larger than 5 MB. Choose a smaller image.");
      return;
    }
    setUploadError(null);
    const reader = new FileReader();
    // Save the framed image the frame shows, not the raw upload; the user can
    // still reframe it with Edit. An image the browser cannot draw keeps the
    // original so the upload is never lost.
    reader.onload = () => {
      const original = String(reader.result || "");
      frameImageAtCentre(original, aspectRatio, outputWidth)
        .then(onChange)
        .catch(() => onChange(original));
    };
    reader.readAsDataURL(file);
  };

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/png,image/jpeg"
      className="hidden"
      onChange={({ target }) => {
        readFile(target.files?.[0]);
        target.value = "";
      }}
    />
  );

  const emptyPrompt = (
    <label
      className={`flex cursor-pointer flex-col items-center justify-center bg-white text-center ${
        compact
          ? "h-[166px] rounded-[10px] border-2 border-dashed border-cpaGold p-[6px]"
          : "h-[260px] gap-[12px] rounded-[10px] border-2 border-dashed border-cpaGold p-[8px]"
      }`}
      onDragOver={(dragEvent) => dragEvent.preventDefault()}
      onDrop={(dropEvent) => {
        dropEvent.preventDefault();
        readFile(dropEvent.dataTransfer.files?.[0]);
      }}
      onClick={(clickEvent) => {
        clickEvent.preventDefault();
        fileInputRef.current?.click();
      }}
    >
      {compact ? <span className="flex h-[150px] w-full flex-col items-center justify-center rounded-[8px] border border-dashed border-[#9E9E9E] bg-[#F7F7F8] px-[12px] text-[13px] leading-[16px] text-[#3A3A3A]">
        <ImageOutlinedIcon sx={{ color: "#9E9E9E", fontSize: 28, mb: "10px" }} />
        Drag and drop your image here or click to browse — 1280 x 720 PX
      </span> : <>
      <CloudUploadIcon
        sx={{
          color: commonStyles.cpaGold,
          fontSize: 44,
        }}
      />
      <span
        className="text-[15px] font-bold leading-[normal] text-[#0A0A0A]"
      >
        Drag and drop your image here
      </span>
      <span className="text-[14px] leading-[normal] text-[#0A0A0A]">
        or click to <span className="text-cpaGoldDark underline">browse</span>
      </span>
      <span
        className="text-[12px] leading-[normal] tracking-[.04em] text-[#3A3A3A]"
      >
        RECOMMENDED SIZE: 1280 X 720 PX
      </span>
      <span className="text-[12px] leading-[normal] text-[#3A3A3A]">
        PNG or JPG, max 5MB
      </span>
      </>}
    </label>
  );

  // A saved image that is not a renderable URL falls back to the prompt.
  const renderableValue = value && isRenderableImage(value) ? value : "";
  return (
    <div>
      {fileInput}
      {renderableValue ? (
        <div className={compact ? "rounded-[10px] border-2 border-dashed border-cpaGold p-[6px]" : "rounded-[10px] border-2 border-dashed border-cpaGold p-[8px]"}>
          <ImageFrameField
            value={renderableValue}
            onChange={onChange}
            aspectRatio={aspectRatio}
            outputWidth={outputWidth}
            emptyContent={null}
            alt={alt}
            onReplace={() => fileInputRef.current?.click()}
            // Design (15) speaker image: Replace and Edit only (decision Q8).
            onRemove={compact ? undefined : () => onChange("")}
            cropGuides={cropGuides}
            replaceLabel={compact ? "Replace" : "Update Banner"}
            removeLabel="Delete Banner"
            alwaysShowControls={!compact}
            controlSize={compact ? "small" : "default"}
          />
        </div>
      ) : (
        emptyPrompt
      )}
      {uploadError ? <p className="mt-2 text-sm text-red-600">{uploadError}</p> : null}
    </div>
  );
};

// ---------------------------------------------------------------------------

type SpeakerDraft = Omit<EventSpeaker, "id">;

const blankSpeakerDraft: SpeakerDraft = {
  name: "",
  company: "",
  title: "",
  imageUrl: "",
  bio: "",
};

// Gold "This event was duplicated" notice (Create Event Screen.dc.html ~94-101).
const DuplicateEventNotice = ({ community }: { community: boolean }): React.ReactElement => (
  <div role="status" className="mb-[22px] flex max-w-[760px] items-start gap-[12px] rounded-[10px] border border-[#E1A731] px-[16px] py-[14px]">
    <svg viewBox="0 0 24 24" fill="none" stroke="#B7801A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-[1px] h-[20px] w-[20px] shrink-0">
      <circle cx="12" cy="12" r="9.2" />
      <path d="M12 7.5v5.5" />
      <path d="M12 16.5h.01" />
    </svg>
    <div className="flex flex-col gap-[4px]">
      <div className="text-[14px] font-semibold text-[#0A0A0A]">This event was duplicated</div>
      <div className="text-[13px] text-[#0A0A0A] [text-wrap:pretty]">
        {community
          ? "Add a new Event Title for this event. All other details were copied from the original event."
          : "Add a new Event Title and Event Code for this event. All other details were copied from the original event."}
      </div>
    </div>
  </div>
);

const Step1EventDetails = ({
  event,
  setEvent,
  chamberId,
  errors,
  duplicated,
}: WizardStepProps): React.ReactElement => {
  const noun = useCmpOrganizationNoun();
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));
  const ida = useEventIdaGeneration({ event, onUse: update });
  const isCommunity = event.eventKind === CmpEventKindEnum.Community;
  const typeBeforeCommunityRef = useRef<Pick<typeof event, "eventTypeId" | "eventType" | "eventTypeColor" | "publicRegistrationEnabled"> | null>(null);
  const kindLocked = isEventKindLocked(event);

  // ---- Speaker flow (EVT-CREATE-006/007): each speaker profile is saved from
  // an isolated draft, so adding another speaker never overrides prior ones.
  // A working draft saved before the keynote column shows (and saves) the
  // first speaker as keynote, as the website does.
  const speakers = resolveEventSpeakerKeynotes(event.speakers || []);
  const [speakerFormOpen, setSpeakerFormOpen] = useState(false);
  const [savedMessage, setSavedMessage] = useState<{ message: string; highlights: string[] } | null>(null);
  const [editingSpeakerId, setEditingSpeakerId] = useState<string | null>(null);
  const [speakerDraft, setSpeakerDraft] = useState<SpeakerDraft>(blankSpeakerDraft);
  const [speakerErrors, setSpeakerErrors] = useState<Record<string, string>>({});
  const [speakerToDelete, setSpeakerToDelete] = useState<EventSpeaker | null>(null);
  const [speakerDeleted, setSpeakerDeleted] = useState<ReturnType<typeof deletedMessage> | null>(null);
  const reorderSpeakerRow = useEventRowReorder(speakers, (next) => update("speakers", next));

  const patchSpeakerDraft = (patch: Partial<SpeakerDraft>) =>
    setSpeakerDraft((prev) => ({ ...prev, ...patch }));

  const openBlankSpeakerForm = () => {
    setEditingSpeakerId(null);
    setSpeakerDraft(blankSpeakerDraft);
    setSpeakerErrors({});
    setSpeakerFormOpen(true);
  };

  const openEditSpeaker = (speaker: EventSpeaker) => {
    setEditingSpeakerId(speaker.id);
    setSpeakerDraft({
      name: speaker.name,
      company: speaker.company,
      title: speaker.title,
      imageUrl: speaker.imageUrl,
      bio: speaker.bio,
    });
    setSpeakerErrors({});
    setSpeakerFormOpen(true);
  };

  const saveSpeakerProfile = () => {
    const nextErrors: Record<string, string> = {};
    if (!speakerDraft.name.trim()) nextErrors.name = enterFieldMessage("Speaker Name");
    if (!speakerDraft.imageUrl) nextErrors.image = "Add a Speaker Image.";
    if (!stripHtml(speakerDraft.bio)) nextErrors.bio = enterFieldMessage("Speaker Bio");
    setSpeakerErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (editingSpeakerId) {
      update(
        "speakers",
        speakers.map((speaker) =>
          speaker.id === editingSpeakerId ? { ...speaker, ...speakerDraft } : speaker
        )
      );
    } else {
      // New speakers start unchecked, so the design keynote rule applies.
      update("speakers", [...speakers, { id: wizardId("speaker"), ...speakerDraft, isKeynote: false }]);
    }
    setEditingSpeakerId(null);
    setSpeakerDraft(blankSpeakerDraft);
    setSpeakerFormOpen(false);
    const speakerName = speakerDraft.name.trim();
    setSavedMessage({
      message: `Speaker ${speakerName} has been added to the event.`,
      highlights: [speakerName],
    });
  };


  // ---- Additional Registration Questions (EVT-CREATE-009), per the
  // contract.md registrationQuestions model.
  const questions: EventRegistrationQuestion[] = event.registrationQuestions?.length
    ? event.registrationQuestions
    : DEFAULT_REGISTRATION_QUESTIONS;
  const setQuestions = (next: EventRegistrationQuestion[]) =>
    update("registrationQuestions", next);
  const patchQuestion = (
    questionId: string,
    patch: Partial<EventRegistrationQuestion>
  ) =>
    setQuestions(
      questions.map((question) =>
        question.id === questionId ? { ...question, ...patch } : question
      )
    );
  const setChoiceEnabled = (questionId: string, choiceId: string, enabled: boolean) =>
    setQuestions(
      questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              choices: question.choices.map((choice: EventRegistrationQuestionChoice) =>
                choice.id === choiceId ? { ...choice, enabled } : choice
              ),
            }
          : question
      )
    );

  const [questionDraft, setQuestionDraft] = useState<string | null>(null);
  const [mealDraft, setMealDraft] = useState("");
  const [addingMeal, setAddingMeal] = useState(false);

  // Claude Design (8) Create Event Screen.dc.html:1998-1999: turning Q1 off
  // clears its accessibility options; turning Q2 off clears its dietary
  // options and preselected meals. Titles and subtitles are kept, so the
  // preset subtitle is still saved as the public form helper text.
  const setQuestionEnabled = (question: EventRegistrationQuestion, enabled: boolean) => {
    if (enabled || question.kind !== "multiSelect") {
      patchQuestion(question.id, { enabled });
      return;
    }
    const cleared: Partial<EventRegistrationQuestion> = {
      enabled,
      choices: question.choices.map((choice) => ({ ...choice, enabled: false })),
    };
    if (question.id === "question-dietary") {
      cleared.mealOptions = [];
      cleared.preselectMealsEnabled = false;
      setAddingMeal(false);
      setMealDraft("");
    }
    patchQuestion(question.id, cleared);
  };

  return (
    <>
      {/* ------------------------------------------------------------- */}
      {/* Event Details                                                   */}
      {/* ------------------------------------------------------------- */}
      <div>
        <SectionCard>
        <SectionHeading
          title="Event Details"
          className="mb-[22px] [&_p:last-child]:mt-[10px]"
          lines={[
            "Add the details of your event. This information will appear on your website’s Event Calendar.",
            "If you are creating a Community/Member Event, select the option below and complete the applicable event information fields. You will not be able to create tickets or sponsorships for this event.",
          ]}
        />
        {shouldShowDuplicateEventNotice(event, duplicated) ? <DuplicateEventNotice community={isCommunity} /> : null}
      <FormControlLabel sx={{ m: 0, p: "18px", gap: "12px", width: "100%", maxWidth: 560, border: `1px solid ${isCommunity ? "#E1A731" : "#ECECEC"}`, borderRadius: "10px",
        "& .MuiCheckbox-root": { p: 0 }, "& .MuiFormControlLabel-label": { fontSize: 14, lineHeight: "normal", fontWeight: 600, color: "#0A0A0A" } }}
        control={<Checkbox checked={event.eventKind === CmpEventKindEnum.Community} disabled={kindLocked}
        onChange={(_, checked) => setEvent(previous => {
          // CES:2019-2027 (EV15-11): unticking restores the event type chosen
          // before Community was ticked.
          if (checked) {
            typeBeforeCommunityRef.current = {
              eventTypeId: previous.eventTypeId, eventType: previous.eventType, eventTypeColor: previous.eventTypeColor,
              publicRegistrationEnabled: previous.publicRegistrationEnabled,
            };
            return { ...previous, eventKind: CmpEventKindEnum.Community, eventTypeId: "community-member-event", eventType: "Community/Member Event", eventTypeColor: "#2563EB" };
          }
          // A saved Community Draft has no type from before the tick. It keeps
          // a type staff chose, and takes registration back on.
          const restored = typeBeforeCommunityRef.current;
          const keptType = !restored && previous.eventTypeId !== "community-member-event" ? previous : null;
          return { ...previous, eventKind: CmpEventKindEnum.Standard,
            eventTypeId: restored?.eventTypeId || keptType?.eventTypeId || "", eventType: restored?.eventType || keptType?.eventType || "", eventTypeColor: restored?.eventTypeColor || keptType?.eventTypeColor || "#E1A731",
            publicRegistrationEnabled: restored?.publicRegistrationEnabled ?? true };
        })} />}
        label="This is a Community/Member Event" />
      {kindLocked ? (
        // Not in design (Moeen, St. Andrews, Nov 3 2026): a locked box needs its reason.
        <div className="mt-[8px] max-w-[560px] text-[13px] text-[#0A0A0A]">
          This can only be changed while an event is a Draft with no sales. To change it, go to Manage Events, click Actions &gt; Duplicate Event, and change it on the copy.
        </div>
      ) : null}
          <div className="mt-[22px]">
          <div className="grid grid-cols-1 gap-x-[20px] gap-y-[22px] md:gap-y-[6px] md:grid-cols-2 md:grid-rows-[auto_auto_auto_auto]">
            <EventLibrarySelect
              chamberId={chamberId}
              kind={CmpEventLibraryKindEnum.Title}
              label="Event Title"
              required
              // Claude Design (8) Create Event Screen.dc.html:113-121; "show"
              // is corrected to "shown" (Decision Q18).
              hint="Add a new event title or select previous event title from the dropdown this will automatically be shown on the event hero banner"
              placeholder="Select Event Title"
              searchPlaceholder="Search Event Title"
              previousEventTitles={{ currentEventId: event.id }}
              value={event.title}
              onChange={(value) => update("title", value)}
              error={errors.title}
              hintAddon={
                // Create Event Screen.dc.html:116-119.
                <FormControlLabel
                  sx={{ m: 0, gap: "10px", alignItems: "center", "& .MuiCheckbox-root": { p: 0 },
                    "& .MuiFormControlLabel-label": { fontSize: 13, lineHeight: "normal", color: "#0A0A0A" } }}
                  control={<Checkbox size="small" checked={event.hideHeroTitle === true}
                    onChange={(_, checked) => update("hideHeroTitle", checked)} />}
                  label="Check if you do not want the event title to be shown on the hero banner."
                />
              }
            />
            <EventTypeSelect
              chamberId={chamberId}
              value={event.eventTypeId}
              currentLabel={event.eventType}
              currentColor={event.eventTypeColor}
              onChange={({ id, label, color }) =>
                setEvent((prev) => ({
                  ...prev,
                  eventTypeId: id,
                  eventType: label,
                  eventTypeColor: color,
                }))
              }
              error={errors.eventType}
            />
          </div>

          {isCommunity ? (
            <div className="mt-[22px] flex w-full max-w-[560px] flex-col gap-[6px]">
              {/* Optional (Create Event Screen.dc.html:145). */}
              <FieldLabel
                note="(optional)"
                className="flex flex-col gap-[4px] [&>span]:leading-[normal]"
                hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]"
                hint="Enter the event page URL. This link will be added to a button on the Hero Banner, directing users to the Community/Member Event page."
              >
                Event Button Link
              </FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={event.externalEventUrl || ""}
                onChange={({ target }) => update("externalEventUrl", target.value)}
                onBlur={() => update("externalEventUrl", normalizeEventButtonUrl(event.externalEventUrl))}
                placeholder="https://"
                error={Boolean(errors.externalEventUrl)}
                helperText={errors.externalEventUrl}
                sx={wizardTextFieldSx}
              />
            </div>
          ) : (
            <div className="mt-[22px] w-full max-w-[560px]">
              {/* Event code is optional (Thunder Bay request, Sept 22 2026);
                  invoices fall back to the event name when it is blank.
                  Copy per Create Event Screen.dc.html:148-156. */}
              <EventLibrarySelect
                chamberId={chamberId}
                kind={CmpEventLibraryKindEnum.Code}
                label="Event Code"
                hint="Add a new code or select an existing code."
                placeholder="Select Event Code"
                searchPlaceholder="Search Event Code"
                error={errors.eventCode}
                value={event.eventCode || ""}
                onChange={(value) => update("eventCode", value)}
              />
            </div>
          )}

          <div className="mt-[22px]">
            <div className="mb-[6px] flex flex-wrap items-start justify-between gap-[16px]">
              <div className="min-w-0 flex-[1_1_280px]">
                {/* Optional (Create Event Screen.dc.html:170, 194). */}
                <FieldLabel
                  note="(Do not add teaser details or description if your banner includes full graphics.)"
                  noteClassName="font-normal"
                  className="flex flex-col gap-[4px] [&>span]:leading-[normal]"
                  hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]"
                  hint="Enter a brief teaser of the event to help attendees understand what to expect and why they should attend. Need help writing it? Use your Intelligent Digital Assistant, IDA, to generate or improve your event teaser."
                >
                  Brief Event Teaser for Hero Banner
                </FieldLabel>
              </div>
              <IdaGenerateControl
                onClick={() => void ida.generate("heroShortDescription")}
                loading={ida.loading && ida.field === "heroShortDescription"}
                disabled={ida.loading}
              />
            </div>
            <RichTextEditor variant="event"
              value={event.heroShortDescription || ""}
              error={Boolean(errors.heroShortDescription)}
              onChange={(html) => update("heroShortDescription", html)}
              placeholder="Enter a brief teaser highlighting what attendees can expect from the event."
              minHeight={120}
              // CES:194 "{n}/250 characters" is a counter, not a limit (EV15-13).
              maxLength={250}
              enforceMaxLength={false}
            />
          </div>

          <div className="mt-[22px]">
            {/* Create Event Screen.dc.html:192-193. The title sponsor logo
                sits on the left side of the event countdown banner on the
                website, so that sentence follows the build (Decision Q27). */}
            <FieldLabel className="mb-[10px] flex flex-col gap-[6px] [&>span]:leading-[normal]" hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]" hint="Upload an eye-catching image to appear in the background of the Event Hero Banner. Your event name and teaser description will appear on top of it, so for best results use an image with no words. Your title sponsor logo can also be added on the left side of the event countdown banner. You can preview the event before making it live on your website.">
              Add Event Hero Banner Image to be used as Background Image
            </FieldLabel>
            <ImageDropzone
              value={event.featuredBanner}
              onChange={(dataUrl) => update("featuredBanner", dataUrl)}
              aspectRatio={16 / 9}
              outputWidth={1280}
              alt="Event hero banner"
              cropGuides={getEventHeroCropGuides(16 / 9)}
            />
          </div>
          </div>
        </SectionCard>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Event Heading (saved as registrationDetailsHtml)                */}
      {/* ------------------------------------------------------------- */}
      <SectionCard>
        {/* Claude Design (8) Create Event Screen.dc.html:216-232. Same
            size/weight as the sibling field labels (EVT-CREATE-005). */}
        <FieldLabel
          required
          hint="Enter a captivating heading to capture the type of event being offered. The heading will appear above the event description provided below on your event page on the website. For example, Network and Connect or Lunch and Learn."
        >
          Event Heading
        </FieldLabel>
        <RichTextEditor variant="event"
          value={event.registrationDetailsHtml || ""}
              error={Boolean(errors.registrationDetailsHtml)}
          onChange={(html) => update("registrationDetailsHtml", html)}
          placeholder="Enter a captivating heading to capture the type of event being offered."
          // CES:238 "{n}/50 characters" is a counter, not a limit (EV15-13).
          maxLength={50}
          enforceMaxLength={false}
          minHeight={120}
        />
        {/* A red border alone does not say what is missing (audit 3.2 item 7). */}
        {errors.registrationDetailsHtml ? (
          <p className="mt-2 text-sm text-red-600">{errors.registrationDetailsHtml}</p>
        ) : null}
        <div className="mt-[16px]">
          <div className="flex flex-wrap items-start justify-between gap-[16px]">
            {/* Create Event Screen.dc.html:244-246, 259. A recommendation,
                not a limit (Monday 13187099680). */}
            <FieldLabel className="mb-[6px] min-w-0 flex-1 basis-[280px]" required
              note="(For the best display for your website’s Event Details page, we recommend limiting the description to 500 characters or fewer.)"
              noteClassName="font-normal" hint={`Enter a brief description of the event to help attendees understand what to expect and why they should attend. Event details like date, time and agenda are displayed elsewhere, so keep the description brief and compelling. Need help writing it? Use your Intelligent Digital Assistant, IDA, to generate or improve your ${noun.Singular}’s event description.`}>
              Brief Event Description
            </FieldLabel>
            <IdaGenerateControl
              onClick={() => void ida.generate("description")}
              loading={ida.loading && ida.field === "description"}
              disabled={ida.loading}
            />
          </div>
          <RichTextEditor variant="event"
            value={event.description}
            error={Boolean(errors.description)}
            onChange={(html) => update("description", html)}
            placeholder="Enter a brief description to help attendees understand what to expect from the event."
            minHeight={120}
          />
          {errors.description ? (
            <p className="mt-2 text-sm text-red-600">{errors.description}</p>
          ) : null}
        </div>
      </SectionCard>

      {!isCommunity && <>
      <EventPartnersSection
        partners={event.partners || []}
        onChange={(partners) => update("partners", partners)}
        onSaved={() => setSavedMessage({ message: "Partner added to the event.", highlights: [] })}
      />

      {/* ------------------------------------------------------------- */}
      {/* Add Keynote Speaker or Create Speaker Panel, with the Speakers  */}
      {/* List in the same card (Create Event Screen.dc.html:320-408)     */}
      {/* ------------------------------------------------------------- */}
      <SectionCard className="flex flex-col gap-[16px]">
        <SectionHeading
          title="Add Keynote Speaker or Create Speaker Panel"
          lines={[
            "Add a profile image and brief bio for each speaker. For a keynote, add one speaker profile. For a panel, click + Add Speaker Profile to add each speaker. Every saved speaker appears in the Speakers List below and on your event page on your website.",
          ]}
        />
        <button
          type="button"
          onClick={openBlankSpeakerForm}
          className="flex items-center gap-[6px] self-start rounded-[8px] border-0 bg-[#E1A731] px-[16px] py-[10px] text-[13px] font-semibold text-white hover:bg-[#C8912A]"
        >
          + Add Speaker Profile
        </button>

        {speakerFormOpen ? (
          <div className="rounded-[12px] border border-[#ECECEC] p-[24px]">
            <div className="grid grid-cols-1 gap-[20px] md:grid-cols-3">
              <div>
                <FieldLabel className="mb-[6px]" required>Speaker Name</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Speaker Name"
                  value={speakerDraft.name}
                  onChange={({ target }) => patchSpeakerDraft({ name: target.value })}
                  error={Boolean(speakerErrors.name)}
                  helperText={speakerErrors.name}
                  sx={wizardTextFieldSx}
                />
              </div>
              <div>
                <FieldLabel className="mb-[6px]">Company Name</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Company Name"
                  value={speakerDraft.company}
                  onChange={({ target }) => patchSpeakerDraft({ company: target.value })}
                  sx={wizardTextFieldSx}
                />
              </div>
              <div>
                <FieldLabel className="mb-[6px]">Title</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Speaker Title"
                  value={speakerDraft.title}
                  onChange={({ target }) => patchSpeakerDraft({ title: target.value })}
                  sx={wizardTextFieldSx}
                />
              </div>
            </div>

            <div className="mt-[16px] grid grid-cols-1 gap-[20px] md:grid-cols-[220px_minmax(0,1fr)]">
              <div>
                <FieldLabel className="mb-[6px]" required>Speaker Image</FieldLabel>
                <ImageDropzone
                  compact
                  value={speakerDraft.imageUrl}
                  onChange={(dataUrl) => patchSpeakerDraft({ imageUrl: dataUrl })}
                  aspectRatio={1}
                  outputWidth={800}
                  alt="Speaker image"
                />
                {speakerErrors.image ? (
                  <p className="mt-2 text-sm text-red-600">{speakerErrors.image}</p>
                ) : null}
              </div>
              <div>
                <FieldLabel className="mb-[6px]" required>Speaker Bio</FieldLabel>
                <RichTextEditor variant="event"
                  value={speakerDraft.bio}
                  onChange={(html) => patchSpeakerDraft({ bio: html })}
                  placeholder="Enter a brief bio for speaker which will display on event page."
                  maxLength={500}
                  minHeight={120}
                />
                {speakerErrors.bio ? (
                  <p className="mt-2 text-sm text-red-600">{speakerErrors.bio}</p>
                ) : null}
              </div>
            </div>

            <div className="mt-[16px] flex flex-wrap gap-[10px]">
              <WizardActionButton tone="gold" sx={{ minHeight: 38, fontSize: 13, px: "16px" }} onClick={saveSpeakerProfile}>
                Save Speaker Profile
              </WizardActionButton>
              <WizardActionButton tone="outline" sx={{ minHeight: 38, fontSize: 13, px: "16px" }} onClick={() => {
                setSpeakerFormOpen(false); setEditingSpeakerId(null); setSpeakerDraft(blankSpeakerDraft); setSpeakerErrors({});
              }}>Cancel</WizardActionButton>
            </div>
          </div>
        ) : null}

        {/* Speakers List (design :378-406), inside the speakers card. */}
        {speakers.length > 0 ? (
        <>
          <SectionHeading
            title="Speakers List"
            lines={[
              "Manage speaker profiles and arrange the order they appear on the event registration page. Identify main keynote speaker so they will be displayed on the website above the remaining speakers.",
            ]}
          />
          {/* Create Event Screen.dc.html:389-406: drag handle column and the
              Main Keynote Speaker column (only one speaker can be checked). */}
          <div className="overflow-x-auto rounded-[10px] border border-[#ECECEC] bg-white">
            <div className="min-w-[980px]">
              <div className="grid grid-cols-[24px_110px_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)_110px_150px] gap-3 bg-[#F7F7F8] px-[16px] py-[12px] text-[11px] font-semibold uppercase tracking-[.05em] text-[#0A0A0A]">
                <span />
                <span>Speaker Image</span>
                <span>Speaker Name</span>
                <span>Company Name</span>
                <span>Title</span>
                <span>Bio</span>
                <span>Main Keynote Speaker</span>
                <span>Actions</span>
              </div>
              {speakers.map((speaker, index) => {
                const reorder = reorderSpeakerRow(index);
                return (
                <div
                  key={speaker.id}
                  {...reorder.rowProps}
                  className="grid grid-cols-[24px_110px_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)_110px_150px] items-center gap-3 border-t border-[#ECECEC] px-[16px] py-[12px]"
                >
                  <EventReorderHandle {...reorder.handleProps} />
                  <span className="flex items-center gap-3">
                    {isRenderableImage(speaker.imageUrl) ? (
                      <img
                        src={speaker.imageUrl}
                        alt={speaker.name}
                        className="h-[44px] w-[44px] rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-[#ECECEC] text-[14px] font-semibold text-[#727272]">{speaker.name.split(/\s+/).map(word => word[0]).slice(0, 2).join("")}</span>
                    )}
                  </span>
                  <span className="truncate text-[13px] font-semibold text-gray-950">
                    {speaker.name}
                  </span>
                  <span className="truncate text-[13px] text-gray-950">
                    {speaker.company}
                  </span>
                  <span className="truncate text-[13px] text-gray-950">
                    {speaker.title}
                  </span>
                  <span className="truncate text-[13px] text-gray-950">
                    {stripHtml(speaker.bio)}
                  </span>
                  <span className="flex items-center">
                    <Checkbox
                      size="small"
                      sx={{ p: 0 }}
                      checked={speaker.isKeynote === true}
                      onChange={(_, checked) => update("speakers", setEventSpeakerKeynote(speakers, speaker.id, checked))}
                      inputProps={{ title: "Main keynote speaker", "aria-label": `Main keynote speaker: ${speaker.name}` }}
                    />
                  </span>
                  <span className="flex gap-2">
                    <WizardActionButton
                      tone="gold"
                      size="small"
                      sx={{ minHeight: 28, fontSize: 11, px: "10px" }}
                      onClick={() => openEditSpeaker(speaker)}
                    >
                      Edit
                    </WizardActionButton>
                    <WizardActionButton
                      tone="red"
                      size="small"
                      sx={{ minHeight: 28, fontSize: 11, px: "10px" }}
                      onClick={() => setSpeakerToDelete(speaker)}
                    >
                      Delete
                    </WizardActionButton>
                  </span>
                </div>
                );
              })}
            </div>
          </div>
        </>
        ) : null}
      </SectionCard>

      <DeleteConfirmModal
        open={Boolean(speakerToDelete)}
        // Design (15) CES:2575, 1833: "Delete Speaker" and the success pop-up.
        heading="Delete Speaker"
        name={speakerToDelete?.name || ""}
        onConfirm={() => {
          if (speakerToDelete) {
            setSpeakerDeleted(deletedMessage("Speaker", speakerToDelete.name || "this speaker"));
            update(
              "speakers",
              speakers.filter((speaker) => speaker.id !== speakerToDelete.id)
            );
            if (editingSpeakerId === speakerToDelete.id) {
              setEditingSpeakerId(null);
              setSpeakerDraft(blankSpeakerDraft);
              setSpeakerFormOpen(false);
            }
          }
          setSpeakerToDelete(null);
        }}
        onCancel={() => setSpeakerToDelete(null)}
      />
      <SuccessPopup
        open={Boolean(speakerDeleted)}
        title={speakerDeleted?.message || ""}
        highlights={speakerDeleted?.highlights}
        onClose={() => setSpeakerDeleted(null)}
      />

      {/* ------------------------------------------------------------- */}
      {/* Registration Settings: Registration Requirements, Event         */}
      {/* Visibility and Event Dress Code share one card (design :410-450) */}
      {/* ------------------------------------------------------------- */}
      <SectionCard className="flex flex-col gap-[20px]">
      <EventRegistrationRequirementsControl
        enabled={event.publicRegistrationEnabled}
        onChange={(enabled) => update("publicRegistrationEnabled", enabled)}
      />

      {/* Event Visibility (EVT-CREATE-008): nothing is selected for a new
          event (design :1690). Publish, Schedule and Update require a
          choice; Save Draft does not (Decision Q12). */}
      <div className="flex flex-col gap-[8px]">
        <SectionHeading
          title="Event Visibility"
          required
          className="[&_p]:mt-[8px]"
          lines={[
            "Control who can see this event. Public events will be shown on website for anyone to register. Private events will generate a link to access and register for event.",
          ]}
        />
        <RadioGroup
          row
          value={event.visibility}
          onChange={({ target }) =>
            update("visibility", target.value as typeof event.visibility)
          }
          sx={{ mt: "4px" }}
        >
          <FormControlLabel
            value="Public"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mr: "40px", "& .MuiRadio-root": { pt: 0.25 } }}
            label={
              <span className="block">
                <span className="block text-[14px] font-semibold text-[#0A0A0A]">
                  Public
                </span>
                <span className="block text-[13px] leading-5 text-[#0A0A0A]">
                  Anyone can view and register
                </span>
              </span>
            }
          />
          <FormControlLabel
            value="Private"
            control={<Radio />}
            sx={{ alignItems: "flex-start", "& .MuiRadio-root": { pt: 0.25 } }}
            label={
              <span className="block">
                <span className="block text-[14px] font-semibold text-[#0A0A0A]">
                  Private
                </span>
                <span className="block text-[13px] leading-5 text-[#0A0A0A]">
                  Only people with the link can view and register
                </span>
              </span>
            }
          />
        </RadioGroup>
      </div>

      {/* Event Dress Code: the design heading is 15px here (design :442). */}
      <div className="flex flex-col gap-[8px]">
        <SectionHeading
          title="Event Dress Code"
          className="[&_h3]:text-[15px] [&_p]:mt-[8px]"
          lines={[
            "To show a dress requirement for your event that will appear on your registration form, check off the appropriate option. If left blank it will not show on registration form",
          ]}
        />
        <RadioGroup
          row
          value={event.dressCode || ""}
          onChange={({ target }) => update("dressCode", target.value as EventDressCode)}
          sx={{ mt: "4px" }}
        >
          {dressCodes.map((code) => (
            <FormControlLabel
              key={code}
              value={code}
              control={<Radio />}
              sx={{ mr: "40px" }}
              label={
                <span className="text-[14px] text-[#0A0A0A]">{code}</span>
              }
            />
          ))}
        </RadioGroup>
      </div>
      </SectionCard>

      {/* ------------------------------------------------------------- */}
      {/* Additional Registration Questions (EVT-CREATE-009), one card     */}
      {/* per Create Event Screen.dc.html:452-549. Q1 and Q2 have no Edit   */}
      {/* button and no subtitle in the UI (Decision Q24).                  */}
      {/* ------------------------------------------------------------- */}
      <SectionCard className="flex flex-col gap-[16px]">
        <SectionHeading
          title="Additional Registration Questions"
          lines={[
            "Enable and select which options to appear on the registration form for attendees to select",
          ]}
        />
        <div className="hidden lg:grid lg:grid-cols-[minmax(0,1fr)_220px_120px] items-center gap-[16px] border-y border-dashed border-[#D9D9D9] py-[12px]">
          <span className="text-[14px] font-semibold text-[#0A0A0A]">Questions</span>
          <span className="text-[15px] font-semibold text-[#0A0A0A]">
            Enable Questions
          </span>
          <span aria-hidden="true" />
        </div>
        {questions.map((question, questionIndex) => {
          const isSpeakerQuestion = question.kind === "speakerQuestion";
          const isDietary = question.id === "question-dietary";
          const meals = question.mealOptions || [];
          const mealsOn = meals.length > 0;
          // Dietary options lock while Q2 is off or preselected meals are in
          // use or being added; accessibility options lock while Q1 is off
          // (design :2487-2488).
          const choicesGated = !question.enabled || (isDietary && (hasPreselectMeals(question) || addingMeal));
          // The meal area locks while Q2 is off or any dietary option is
          // ticked (design :2457).
          const mealControlsDisabled = !question.enabled || question.choices.some((choice) => choice.enabled);
          const mealDimClassName = mealControlsDisabled ? "pointer-events-none opacity-[0.45]" : "";
          const trimmedMeal = mealDraft.trim();
          const mealSaveDisabled = !trimmedMeal || meals.includes(trimmedMeal);
          return (
            <div key={question.id} className={questionIndex > 0 ? "border-t border-dashed border-[#D9D9D9] pt-[16px]" : ""}>
              <div className={questionGridClassName}>
                <div>
                  <p className="text-[14px] font-semibold text-[#0A0A0A]">{question.adminTitle}</p>
                  {isSpeakerQuestion ? (
                    <p className="mt-[6px] text-[13px] leading-5 text-[#0A0A0A]">{SPEAKER_QUESTION_HELPER}</p>
                  ) : null}
                  {question.kind === "multiSelect" ? (
                    <div className="mt-[10px] flex flex-col items-start gap-[10px]">
                      {question.choices.map((choice: EventRegistrationQuestionChoice) => (
                        <div key={choice.id}><FormControlLabel sx={questionChoiceSx(choicesGated)}
                          control={<Checkbox checked={choice.enabled}
                            inputProps={{ "aria-label": `Enable ${choice.label} option for ${question.adminTitle}` }}
                            disabled={choicesGated}
                            onChange={(_, checked) => setChoiceEnabled(question.id, choice.id, checked)} />}
                          label={choice.label} /></div>
                      ))}
                      {isDietary ? (
                        <div className="mt-[4px] flex w-full flex-col gap-[10px] pt-[12px]">
                          <p className="text-[13px] font-semibold text-[#0A0A0A]">When the above dietary options are added, the preselected meal options are not available</p>
                          <div className={`flex flex-wrap items-center gap-[12px] ${mealDimClassName}`}>
                            <button type="button" disabled={mealControlsDisabled}
                              onClick={() => {
                                setAddingMeal(true);
                                // Adding a meal clears the dietary options (design :2472).
                                if (question.choices.some((choice) => choice.enabled)) {
                                  patchQuestion(question.id, { choices: question.choices.map((choice) => ({ ...choice, enabled: false })) });
                                }
                              }}
                              className="rounded-[8px] border border-[#E1A731] bg-white px-[14px] py-[8px] text-[12px] font-semibold text-[#0A0A0A] hover:bg-[#FDF6E7]">
                              +Add Preselect Meal Option
                            </button>
                          </div>
                          <span className="text-[13px] text-[#0A0A0A]">
                            {mealsOn || addingMeal
                              ? "Attendees can select one of the available options on the event page. The dietary options above are disabled while this is enabled."
                              : "Enter the preselected meal options available for this event. When enabled, attendees can select one of the available options on the event page."}
                          </span>
                          {mealsOn && <div className={`flex flex-col gap-[8px] ${mealDimClassName}`}>
                            {meals.map((meal, index) => (
                              <div key={meal} className="flex items-center justify-between gap-[10px] rounded-[8px] border border-[#ECECEC] bg-[#FAFAFA] px-[12px] py-[9px]">
                                <span className="truncate text-[13px] text-[#0A0A0A]" title={meal}>{meal}</span>
                                <button type="button" className="shrink-0 rounded-[6px] border-0 bg-transparent px-[9px] py-[5px] text-[12px] font-semibold text-[#F21B1B]"
                                  onClick={() => {
                                    const nextMeals = meals.filter((_, row) => row !== index);
                                    patchQuestion(question.id, { mealOptions: nextMeals, preselectMealsEnabled: nextMeals.length > 0 });
                                  }}>
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>}
                          {addingMeal && <div className={`flex flex-col gap-[8px] ${mealDimClassName}`}>
                            <input type="text" maxLength={250} value={mealDraft} aria-label="Preselect meal option"
                              onChange={({ target }) => setMealDraft(target.value)} placeholder="Example: Hamburger and Soft Drink"
                              className="w-full rounded-[8px] border border-[#9E9E9E] px-[12px] py-[9px] text-[13px] text-[#0A0A0A] outline-none focus:border-cpaGold" />
                            <div className="flex flex-wrap gap-[8px]">
                              <button type="button" disabled={mealSaveDisabled}
                                className="rounded-[8px] border-0 bg-[#E1A731] px-[16px] py-[9px] text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => {
                                  patchQuestion(question.id, { mealOptions: [...meals, trimmedMeal], preselectMealsEnabled: true,
                                    choices: question.choices.map(choice => ({ ...choice, enabled: false })) });
                                  setAddingMeal(false); setMealDraft("");
                                }}>Save</button>
                              <button type="button" className="rounded-[8px] border border-[#ECECEC] bg-white px-[14px] py-[9px] text-[12px] font-semibold text-[#0A0A0A]"
                                onClick={() => { setAddingMeal(false); setMealDraft(""); }}>Cancel</button>
                            </div>
                          </div>}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <Switch
                    checked={question.enabled}
                    sx={eventSwitchSx}
                    inputProps={{ "aria-label": `Enable question: ${question.adminTitle}` }}
                    onChange={({ target }) => setQuestionEnabled(question, target.checked)}
                  />
                  <span className="text-[12px] font-semibold tracking-wide" style={{ color: question.enabled ? "#10B981" : "#727272" }}>
                    {question.enabled ? "ENABLED" : "DISABLED"}
                  </span>
                </div>
                <span aria-hidden="true" />
              </div>

              {isSpeakerQuestion ? (
                <fieldset disabled={!question.enabled} className="mt-[16px] min-w-0 rounded-[12px] border border-[#ECECEC] bg-white p-[24px] disabled:opacity-[0.45]">
                  <h4 className="text-[15px] font-semibold text-[#0A0A0A]">
                    Submit a Question
                  </h4>
                  <p className="mt-[10px] text-[13px] leading-5 text-[#0A0A0A]">
                    {question.helperText ||
                      "Submit a question you would like the speaker or panellists to address. We’ll do our best to include submitted questions, but selection cannot be guaranteed."}
                  </p>
                  <input
                    className="mt-[10px] w-full rounded-[8px] border border-[#9E9E9E] px-[12px] py-[10px] text-[14px] text-[#0A0A0A] focus:border-cpaGold focus:outline-none"
                    placeholder="Enter question?"
                    maxLength={250}
                    value={questionDraft ?? question.label ?? ""}
                    onChange={({ target }) => setQuestionDraft(target.value)}
                  />
                  <p className="mt-2 text-[13px] text-gray-500">
                    {(questionDraft ?? question.label ?? "").length}/250 characters
                  </p>
                  <div className="mt-5">
                    <WizardActionButton
                      tone="gold"
                      sx={{ minHeight: 34, fontSize: 12, px: "14px" }}
                      disabled={!question.enabled}
                      onClick={() => {
                        patchQuestion(question.id, { label: questionDraft ?? question.label ?? "" });
                        setQuestionDraft(null);
                      }}
                    >
                      Save Question
                    </WizardActionButton>
                  </div>
                </fieldset>
              ) : null}
            </div>
          );
        })}
      </SectionCard>

      </>}
      <ActionSuccessDialog
        message={savedMessage?.message}
        highlights={savedMessage?.highlights}
        onClose={() => setSavedMessage(null)}
      />
      <IdaGenerateDialog
        open={ida.field !== null}
        title={`Let Me Generate Your ${EVENT_IDA_FIELDS[ida.field || "description"].label}`}
        description="I use your event title and entered event details to suggest a description. Review and edit it, then click Use to apply it to this field."
        fieldLabel="Suggested event description"
        maxLength={EVENT_IDA_FIELDS[ida.field || "description"].maxLength}
        value={ida.value}
        onChange={ida.setValue}
        onUse={ida.useDescription}
        onRegenerate={() => { if (ida.field) void ida.generate(ida.field); }}
        onClose={ida.close}
        loading={ida.loading}
        error={ida.error}
      />
    </>
  );
};

export default Step1EventDetails;
